/**
 * IP → language helpers shared by the dashboard language suggestion popup
 * and onboarding defaults (POC-655).
 *
 * Free geo lookup via api.country.is (no Crowdin / paid geo vendor).
 * Buyer-facing sales/checkout must never auto-apply these results — see
 * `services/i18n.ts` and POC-407.
 */

export const SUPPORTED_LANGUAGES = [
	"en",
	"fr",
	"de",
	"it",
	"pt",
	"nl",
	"es",
	"zh",
	"zh-Hant",
	"ja",
	"ko",
	"ar",
	"hi",
	"id",
	"tr",
	"pl",
	"vi",
	"th",
	"ru",
	"uk",
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const isSupportedLanguage = (
	lng: string | null | undefined,
): lng is SupportedLanguage =>
	!!lng && (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);

/** Right-to-left scripts among the supported languages. */
export const RTL_LANGUAGES: ReadonlySet<string> = new Set(["ar"]);

/** Chinese tags that mean Traditional script (Taiwan, Hong Kong, Macau). */
const TRADITIONAL_CHINESE = /^zh[-_](hant|tw|hk|mo)\b/i;

/**
 * Any language tag ("pt-BR", "zh-TW", "zh_Hant_HK", "fr") → the supported
 * language it should use, or undefined. Region tags collapse to the primary
 * subtag, except Traditional Chinese, which keeps its own catalog.
 */
export function toSupportedLanguage(
	tag: string | null | undefined,
): SupportedLanguage | undefined {
	if (!tag) return undefined;
	const value = tag.trim();
	if (isSupportedLanguage(value)) return value;
	if (TRADITIONAL_CHINESE.test(value)) return "zh-Hant";
	const primary = value.split(/[-_]/)[0]?.toLowerCase();
	return isSupportedLanguage(primary) ? primary : undefined;
}

/** Country code (ISO 3166-1 alpha-2) → supported language */
export const COUNTRY_TO_LANGUAGE: Record<string, SupportedLanguage> = {
	US: "en",
	GB: "en",
	AU: "en",
	CA: "en",
	NZ: "en",
	IE: "en",
	ZA: "en",
	SG: "en",
	PH: "en",
	MY: "en",
	NG: "en",
	KE: "en",
	GH: "en",
	UG: "en",
	TZ: "en",
	ZW: "en",
	FR: "fr",
	BE: "fr",
	LU: "fr",
	MC: "fr",
	CI: "fr",
	SN: "fr",
	ML: "fr",
	BF: "fr",
	NE: "fr",
	TD: "fr",
	CD: "fr",
	CM: "fr",
	MG: "fr",
	HT: "fr",
	RW: "fr",
	TG: "fr",
	BJ: "fr",
	GN: "fr",
	DJ: "fr",
	GA: "fr",
	CG: "fr",
	BI: "fr",
	DE: "de",
	AT: "de",
	CH: "de",
	LI: "de",
	IT: "it",
	SM: "it",
	VA: "it",
	PT: "pt",
	BR: "pt",
	AO: "pt",
	MZ: "pt",
	CV: "pt",
	GW: "pt",
	TL: "pt",
	ST: "pt",
	NL: "nl",
	SR: "nl",
	AW: "nl",
	CW: "nl",
	CN: "zh",
	TW: "zh-Hant",
	HK: "zh-Hant",
	MO: "zh-Hant",
	JP: "ja",
	KR: "ko",
	ID: "id",
	TR: "tr",
	PL: "pl",
	VN: "vi",
	TH: "th",
	RU: "ru",
	BY: "ru",
	KZ: "ru",
	KG: "ru",
	UA: "uk",
	ES: "es",
	MX: "es",
	AR: "es",
	CO: "es",
	CL: "es",
	PE: "es",
	VE: "es",
	EC: "es",
	GT: "es",
	CU: "es",
	BO: "es",
	DO: "es",
	HN: "es",
	PY: "es",
	SV: "es",
	NI: "es",
	CR: "es",
	PA: "es",
	UY: "es",
	PR: "es",
	GQ: "es",
	SA: "ar",
	AE: "ar",
	EG: "ar",
	IQ: "ar",
	JO: "ar",
	KW: "ar",
	LB: "ar",
	LY: "ar",
	MA: "ar",
	OM: "ar",
	QA: "ar",
	SY: "ar",
	TN: "ar",
	YE: "ar",
	BH: "ar",
	DZ: "ar",
	PS: "ar",
	SD: "ar",
	MR: "ar",
	SO: "ar",
	KM: "ar",
	IN: "hi",
};

/** Each language in its own name: what the language picker shows first. */
export const LANGUAGE_NATIVE_LABELS: Record<SupportedLanguage, string> = {
	en: "English",
	fr: "Français",
	de: "Deutsch",
	it: "Italiano",
	pt: "Português",
	nl: "Nederlands",
	es: "Español",
	zh: "简体中文",
	"zh-Hant": "繁體中文",
	ja: "日本語",
	ko: "한국어",
	ar: "العربية",
	hi: "हिन्दी",
	id: "Bahasa Indonesia",
	tr: "Türkçe",
	pl: "Polski",
	vi: "Tiếng Việt",
	th: "ไทย",
	ru: "Русский",
	uk: "Українська",
};

/** English names, the fallback when Intl.DisplayNames is unavailable. */
export const LANGUAGE_ENGLISH_NAMES: Record<SupportedLanguage, string> = {
	en: "English",
	fr: "French",
	de: "German",
	it: "Italian",
	pt: "Portuguese",
	nl: "Dutch",
	es: "Spanish",
	zh: "Chinese (Simplified)",
	"zh-Hant": "Chinese (Traditional)",
	ja: "Japanese",
	ko: "Korean",
	ar: "Arabic",
	hi: "Hindi",
	id: "Indonesian",
	tr: "Turkish",
	pl: "Polish",
	vi: "Vietnamese",
	th: "Thai",
	ru: "Russian",
	uk: "Ukrainian",
};

/**
 * A language's name in another language ("es" shown to a French reader →
 * "espagnol"), from the browser's own CLDR data. Falls back to English.
 */
export function languageNameIn(
	language: SupportedLanguage,
	displayLanguage: string,
): string {
	try {
		const names = new Intl.DisplayNames([displayLanguage, "en"], {
			type: "language",
		});
		const tag = language === "zh" ? "zh-Hans" : language;
		const name = names.of(tag);
		if (name && name !== tag) {
			return name.charAt(0).toLocaleUpperCase(displayLanguage) + name.slice(1);
		}
	} catch {
		// old browsers: fall through
	}
	return LANGUAGE_ENGLISH_NAMES[language];
}

const IP_LANG_KEY = "ip-detected-language";
const IP_LANG_TS_KEY = "ip-detected-language-ts";
const IP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Fetch ISO country from IP. Free endpoint — no API key. */
export async function fetchCountryCode(): Promise<string | undefined> {
	try {
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), 3000);
		const res = await fetch("https://api.country.is/", {
			signal: controller.signal,
		});
		clearTimeout(timeout);
		if (!res.ok) return undefined;
		const data = (await res.json()) as { country?: string };
		return data.country?.toUpperCase();
	} catch {
		return undefined;
	}
}

export function languageForCountry(
	country: string | undefined,
): SupportedLanguage | undefined {
	if (!country) return undefined;
	return COUNTRY_TO_LANGUAGE[country.toUpperCase()];
}

/**
 * Resolve a suggested language from IP (cached 7 days). Does NOT apply it —
 * callers show a confirmation popup (POC-655).
 */
export async function detectSuggestedLanguageFromIp(): Promise<
	SupportedLanguage | undefined
> {
	try {
		const cached = localStorage.getItem(IP_LANG_KEY);
		const ts = Number(localStorage.getItem(IP_LANG_TS_KEY) || 0);
		const isFresh = Boolean(ts && Date.now() - ts < IP_TTL_MS);
		if (isSupportedLanguage(cached) && isFresh) {
			return cached;
		}
	} catch {
		// private mode / blocked storage
	}

	const country = await fetchCountryCode();
	const lang = languageForCountry(country);
	if (!lang) return undefined;

	try {
		localStorage.setItem(IP_LANG_KEY, lang);
		localStorage.setItem(IP_LANG_TS_KEY, String(Date.now()));
	} catch {
		// ignore
	}
	return lang;
}

export function applyDocumentDirection(lng: string): void {
	if (typeof document === "undefined") return;
	document.documentElement.lang = lng;
	const base = toSupportedLanguage(lng) ?? lng;
	document.documentElement.dir = RTL_LANGUAGES.has(base) ? "rtl" : "ltr";
}
